import type { Channel, DefaultGenerics, ExtendableGenerics, StreamChat } from 'stream-chat';

/** Wait before retry 1, then retry 2. A third failure is returned to the caller. */
const RETRY_DELAYS_MS = [500, 1500] as const;
const TIMEOUT_MESSAGE = /query channels timed out/i;
const installedFlag = Symbol.for('smallsoftware.chat-web.resilientQueryChannels');

type Client<T extends ExtendableGenerics> = StreamChat<T>;
type QueryArgs<T extends ExtendableGenerics> = Parameters<Client<T>['queryChannels']>;
type QueryResult<T extends ExtendableGenerics> = ReturnType<Client<T>['queryChannels']>;

type TimeoutLike = {
    code?: unknown;
    message?: unknown;
    response?: { status?: unknown };
};

function isQueryChannelsTimeout(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return false;
    const candidate = error as TimeoutLike;
    if (typeof candidate.message !== 'string' || !TIMEOUT_MESSAGE.test(candidate.message)) {
        return false;
    }
    // stream-chat copies response.data.code onto err.code. A bare HTTP 500 with the
    // same message is the same failure when the code field was not copied.
    if (candidate.code === -1) return true;
    return candidate.response?.status === 500;
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function requestKey(args: readonly unknown[]): string | undefined {
    try {
        return JSON.stringify(args);
    } catch {
        return undefined;
    }
}

function ownChannelList<T extends ExtendableGenerics>(pending: QueryResult<T>): QueryResult<T> {
    // usePaginatedChannels splices this array in place on delete/hide. Each list
    // needs its own array; the channel objects stay the client-cache instances.
    return pending.then((channels) => [...channels]);
}

async function queryWithRetry<T extends ExtendableGenerics>(
    client: Client<T>,
    original: Client<T>['queryChannels'],
    args: QueryArgs<T>,
): Promise<Channel<T>[]> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            const channels: Channel<T>[] = await original.call(client, args[0], args[1], args[2], args[3]);
            return channels;
        } catch (error) {
            lastError = error;
            const waitMs = RETRY_DELAYS_MS[attempt];
            if (waitMs === undefined || !isQueryChannelsTimeout(error)) {
                throw error;
            }
            await delay(waitMs);
        }
    }
    throw lastError;
}

/**
 * Dedupes identical in-flight queryChannels calls and retries Stream's cold
 * "query channels timed out" failure. Safe to call more than once for a client.
 */
export function installResilientQueryChannels<T extends ExtendableGenerics = DefaultGenerics>(
    client: Client<T>,
): Client<T> {
    if (Reflect.get(client, installedFlag) === true) {
        return client;
    }
    if (typeof client.queryChannels !== 'function') {
        throw new Error('installResilientQueryChannels: client.queryChannels is not a function');
    }

    const original = client.queryChannels;
    const inFlight = new Map<string, QueryResult<T>>();

    const wrapped = function (this: Client<T> | void, ...args: QueryArgs<T>): QueryResult<T> {
        const target = this ?? client;
        const key = requestKey(args);
        if (key) {
            const existing = inFlight.get(key);
            if (existing) return ownChannelList(existing);
        }

        const pending = queryWithRetry(target, original, args);
        if (key) {
            inFlight.set(key, pending);
            const clear = () => {
                if (inFlight.get(key) === pending) inFlight.delete(key);
            };
            pending.then(clear, clear);
        }
        return ownChannelList(pending);
    };

    Object.defineProperty(client, installedFlag, {
        value: true,
        enumerable: false,
    });
    client.queryChannels = wrapped;
    return client;
}
